import { FC, RefObject } from 'react';
import cn from 'classnames';
import styles from '../navheader.module.scss';
import { Button } from '@/ui-components/atoms/Button/Button';
import { SearchIcon } from '@/assets/icons/SearchIcon';
import { HamburgerIcon } from '@/assets/icons/HamburgerIcon';
import { PopoverMenu } from '@/ui-components/molecules/PopoverMenu/PopoverMenu';
import { DownloadButtonProps } from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import { AnonymousAlertSnapshotIcon } from '@/ui-components/organisms/AccountSnapshot/AnonymousAlertSnapshotIcon';
import { AnonymousUserSnapshotIcon } from '@/ui-components/organisms/AccountSnapshot/AnonymousUserSnapshotIcon';
import { NavItem, HelpCenterItem } from '../NavHeader.type';
import { NavLinkItem } from '../navItems/NavLink/NavLinkItem';

interface NavRightSectionProps {
    search?: boolean;
    searchOpen: boolean;
    toggleSearchBar: () => void;
    handleSearchClose: () => void;
    buttonRef: RefObject<HTMLButtonElement | null>;
    isDarkTheme: boolean;
    account?: boolean;
    accountWidget: RefObject<HTMLElement | null>;
    appdownloadItem?: DownloadButtonProps[];
    helpCenterItem?: HelpCenterItem;
    supportTitle?: string;
    helpCenterHovered: boolean;
    setHelpCenterHovered: (val: boolean) => void;
    navDropdownHoveredIndex: number | null;
    setNavDropdownHoveredIndex: (val: number | null) => void;
    trySiriusXMlink?: string;
    navRightCtas?: NavItem[];
    navTheme?: string;
    onPageTop: boolean;
    isSimpleType: boolean;
    setDrawerOpen: (val: boolean) => void;
    handleDrawerToggle: () => void;
}

export const NavRightSection: FC<NavRightSectionProps> = ({
    search,
    searchOpen,
    toggleSearchBar,
    handleSearchClose,
    buttonRef,
    isDarkTheme,
    account,
    accountWidget,
    appdownloadItem,
    helpCenterItem,
    supportTitle,
    helpCenterHovered,
    setHelpCenterHovered,
    navDropdownHoveredIndex,
    setNavDropdownHoveredIndex,
    trySiriusXMlink,
    navRightCtas,
    navTheme,
    onPageTop,
    isSimpleType,
    setDrawerOpen,
    handleDrawerToggle,
}) => (
    <div className={styles.rightContent}>
        <div
            className={cn(styles.iconsWrapper, {
                [styles.darktheme]: isDarkTheme,
                [styles.noAccountSpacing]: !account,
            })}
        >
            {search && (
                <button
                    ref={buttonRef}
                    className={styles.searchIcon}
                    aria-label="Search"
                    title="Search"
                    onClick={toggleSearchBar}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') handleSearchClose();
                    }}
                >
                    <SearchIcon aria-label={searchOpen ? 'Close search' : 'Open search'} />
                </button>
            )}

            {account && (
                <div className={styles.navAccount}>
                    <sxm-ui-nav-account-presence-icons
                        panel-margin-top={18}
                        class=""
                        ref={accountWidget}
                        onClick={handleSearchClose}
                    ></sxm-ui-nav-account-presence-icons>

                    {/* Account snapshot icons placeholder */}
                    {process.env.NODE_ENV === 'development' && (
                        <div className={styles.accountSnapshotIconsDev}>
                            <AnonymousAlertSnapshotIcon />
                            <AnonymousUserSnapshotIcon appdownloadItem={appdownloadItem} />
                        </div>
                    )}
                </div>
            )}

            {!isSimpleType && (
                <button
                    className={styles.hamburgerIcon}
                    onClick={() => {
                        setDrawerOpen(true);
                        handleDrawerToggle();
                    }}
                    aria-label="Hamburger Menu"
                >
                    <HamburgerIcon />
                </button>
            )}
        </div>

        {helpCenterItem && (
            <button
                className={styles.supportButton}
                onMouseEnter={() => setHelpCenterHovered(true)}
                onMouseLeave={() => setHelpCenterHovered(false)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        setHelpCenterHovered(true);
                        setNavDropdownHoveredIndex(null);
                    } else if (e.key === 'Escape') {
                        setNavDropdownHoveredIndex(null);
                        setHelpCenterHovered(false);
                    }
                }}
                aria-expanded={helpCenterHovered}
                aria-label="Support"
                data-type="Navhover_Support"
            >
                <span className={cn(styles.helpCenter, { [styles.darktheme]: isDarkTheme })}>{supportTitle}</span>
                <PopoverMenu {...helpCenterItem} className={styles.supportPopover} open={helpCenterHovered} />
            </button>
        )}

        <div className={styles.buttonWrapper}>
            {trySiriusXMlink && (
                <Button
                    variant="primary"
                    type="button"
                    colorSet={isDarkTheme && navDropdownHoveredIndex === null ? 'subset-sxm-blue' : 'base'}
                    size="small"
                    fitting="hug-contents"
                    href={trySiriusXMlink}
                    aria-label="Try SiriusXM"
                >
                    Try SiriusXM
                </Button>
            )}
            <ul className={styles.headingLinks}>
                {navRightCtas?.map((item, index) => {
                    if (item.link && item.link_type === 'Text') {
                        return (
                            <NavLinkItem
                                index={index}
                                isDarkTheme={isDarkTheme}
                                item={item}
                                key={index}
                                setHelpCenterHovered={setHelpCenterHovered}
                                setNavDropdownHoveredIndex={setNavDropdownHoveredIndex}
                            />
                        );
                    } else if (item.link) {
                        return (
                            <li key={index}>
                                <Button
                                    variant="primary"
                                    type="button"
                                    colorSet={
                                        navTheme === 'Photo' &&
                                        navDropdownHoveredIndex === null &&
                                        onPageTop &&
                                        !searchOpen
                                            ? 'overlay'
                                            : 'base'
                                    }
                                    size="small"
                                    fitting="fill-container"
                                    aria-label={item.aria_label}
                                    href={item.link}
                                    key={index}
                                >
                                    {item.title}
                                </Button>
                            </li>
                        );
                    }
                })}
            </ul>
        </div>
    </div>
);
